import { SsrDetails } from '@tff/types/tff/tfa-amend-ancillary-service';
import { PaxObject, SsrsByPax } from '../../../../types/ancillaryTypes';

/**
 * Determines which SSRs are newly added based on the flow.
 *
 * @param ssrsToAdd - SSRs to be added, organized by passenger ID
 * @param includedSsrs - Already booked SSRs, organized by passenger ID
 * @param rebooking - Whether this is a rebooking flow
 * @param rebookOffers - Rebook offer details containing segment information
 * @param ondArray - Array of origin-destination segments
 * @returns Object containing only the newly added SSRs per passenger
 *
 * @description
 * - Rebook flow: returns all ssrsToAdd of the bound that was rebooked, and only new ssrs for the other bound
 * - Edit Ancillaries flow: returns only new ssrs
 */
export const getNewAddedSsrs = (
  ssrsToAdd: PaxObject,
  includedSsrs: PaxObject,
  rebooking: boolean | undefined,
  rebookOffers: any,
  ondArray: string[],
) => {
  if (rebooking) {
    if (rebookOffers.offerOnd.length === ondArray.length) {
      return ssrsToAdd;
    }
  }

  return getAddedSsrsByFlow(rebooking, ssrsToAdd, includedSsrs, rebookOffers);
};

/**
 * Processes SSRs to identify which ones are newly added based on the flow type.
 *
 * @param rebookFlow - Whether this is a rebooking flow
 * @param ssrsToAdd - SSRs to be added, organized by passenger ID
 * @param bookedSsrs - Already booked SSRs, organized by passenger ID
 * @param rebookOffers - Rebook offer details
 * @returns Object containing only the newly added SSRs per passenger
 *
 * @description
 * Compares ssrsToAdd against bookedSsrs to determine which SSRs are actually new.
 * Handles cases where SSR amounts are increased and passengers with no existing bookings.
 */
const getAddedSsrsByFlow = (
  rebookFlow: boolean | undefined,
  ssrsToAdd: PaxObject,
  bookedSsrs: PaxObject,
  rebookOffers: any,
) => {
  let uniqueItems: PaxObject = {};

  Object.keys(ssrsToAdd).forEach(paxId => {
    if (!bookedSsrs[paxId]) {
      uniqueItems[paxId] = ssrsToAdd[paxId];
      return;
    }

    let newSsrs: any[] = [];
    ssrsToAdd[paxId].forEach(ssr => {
      if (rebookFlow && ssr.segmentOnd === rebookOffers.offerOnd[0]) {
        return newSsrs.push({ ...ssr });
      }
      const matchingSsr = findMatchingSsrByCodeAndSegment(bookedSsrs, ssr, paxId);
      if (matchingSsr && matchingSsr.amount < ssr.amount) {
        const newAmount = ssr.amount - matchingSsr.amount;
        return newSsrs.push({ ...ssr, amount: newAmount });
      }
      if (!matchingSsr) return newSsrs.push({ ...ssr });
    });

    uniqueItems[paxId] = newSsrs;
  });

  return uniqueItems;
};

/**
 * Finds a matching SSR by code and segment for a specific passenger.
 *
 * @param bookedSsrs - Already booked SSRs organized by passenger ID
 * @param ssr - The SSR to find a match for
 * @param paxId - The passenger ID
 * @returns The matching booked SSR if found, undefined otherwise
 *
 * @description
 * Compares both SSR code and segment to find an exact match.
 * This ensures SSRs for different flight segments are treated as separate items.
 */
const findMatchingSsrByCodeAndSegment = (bookedSsrs: SsrsByPax, ssr: SsrDetails, paxId: string) => {
  return bookedSsrs[paxId].find(element => element.code === ssr.code && element.segmentOnd === ssr.segmentOnd);
};
